import { Component, Input, ViewEncapsulation, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, merge, of } from 'rxjs';
import { map, switchMap, debounceTime, distinctUntilChanged, takeUntil, catchError, filter } from 'rxjs/operators';
import { NUMBER_500 } from '~shared/constants';
import type { IAutocompleteField } from '../../models/advanced-form-fields.model';

export interface AutocompleteItem {
  label: string;
  value: string;
  metadata?: unknown;
}

const DEBOUNCE_TIME = NUMBER_500;

@Component({
  selector: 'app-autocomplete-field',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteFieldComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class AutocompleteFieldComponent implements ControlValueAccessor {
  @Input() id: string;
  @Input() formControlName: string;
  @Input() placeholder: string;
  @Input() search: IAutocompleteField['search'];
  @Input() onOptionClick: IAutocompleteField['onOptionClick'];
  @Input() getItemPrefixUrl?: () => string;
  @Input() onFocus: HTMLInputElement['focus'];
  private _value = '';
  private textSubject$ = new Subject<string>();
  results$ = this.textSubject$.pipe(
    map((query = '') => query.trim()),
    filter<string>(Boolean),
    debounceTime(DEBOUNCE_TIME),
    distinctUntilChanged(),
    switchMap((text) => this.search(text).pipe(takeUntil(this.textSubject$))),
    catchError((error, caught) => merge(of([]), caught))
  );

  public set value(value) {
    this._value = value;
    this.onChange(value);
  }

  public get value(): string {
    return this._value;
  }

  public onChangeValue(event: Event) {
    const value = event.target['value'];
    if (typeof value === 'string') {
      this.textSubject$.next(value);
      this.value = value;
    }
  }

  public handleFocus(): void {
    if (this.onFocus) {
      this.onFocus();
    }
  }

  public onChange = (text: string) => {};

  public onTouched = () => {};

  public writeValue(text: string): void {
    this.value = text;
  }

  registerOnChange(fn: (text: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
